/**
 * GallerySettings.tsx - Gallery Configuration and Settings Interface
 *
 * This component provides a comprehensive settings interface for configuring
 * the public fundraiser gallery including appearance, content filtering, and branding.
 *
 * Key Features:
 * - Gallery appearance customization (title, logo, colors)
 * - Content filtering options (all, active, specific fundraisers)
 * - Timeframe-based filtering for recent fundraisers
 * - Live preview of gallery settings
 * - Settings persistence and validation
 * - Responsive settings interface
 *
 * Settings Categories:
 * - Appearance: Title, logo, background color, text color
 * - Content: Which fundraisers to display and filtering rules
 * - Timeframe: Date-based filtering for recent fundraisers
 * - Specific Selection: Manual fundraiser selection for curated galleries
 *
 * Props: None (manages gallery settings through MobX store)
 *
 * Dependencies: MobX store, gallery settings components, styled-components
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ArrowLeft, Save, Eye, X } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { GalleryAppearanceSection } from "./gallery-settings/GalleryAppearanceSection";
import { GalleryContentSection } from "./gallery-settings/GalleryContentSection";
import { FundraiserService } from "../services";
import { firstValueFrom } from "rxjs";
import { Loader, withRouter } from "@mindmemobile/mm-shared-global";
import { GallerySettingsModel, MediaModel } from "../models/GalleryModels";
import FullLibraryView from "./FullLibraryView";
import { MediaItem } from "@mindmemobile/mm-shared-library";
import { NavigateFunction } from "react-router-dom";
import ToastService from "./ToastService";

const SettingsContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #667eea 0%,
    #764ba2 25%,
    #f093fb 50%,
    #f5576c 75%,
    #4facfe 100%
  );
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(248, 250, 252, 0.95) 100%
    );
    pointer-events: none;
  }
`;

const HeaderContainer = styled.div<{ isScrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${(props) =>
    props.isScrolled
      ? "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%)"
      : "transparent"};
  backdrop-filter: ${(props) => (props.isScrolled ? "blur(20px)" : "none")};
  border-bottom: ${(props) =>
    props.isScrolled ? "1px solid rgba(255, 255, 255, 0.3)" : "none"};
  box-shadow: ${(props) =>
    props.isScrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none"};
  padding: ${(props) => (props.isScrolled ? "16px 0" : "32px 0")};
  transition: all 0.3s ease;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  padding: 0 32px 80px;
  min-height: 100vh;
  max-width: 1400px;
  margin: 0 auto;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-1px);
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button<{ variant?: "primary" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  ${(props) =>
    props.variant === "primary"
      ? `
    background: linear-gradient(135deg, #10b981, #059669);
    color: #ffffff;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
    }
  `
      : `
    background: rgba(255, 255, 255, 0.9);
    color: #64748b;
    border: 1px solid rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 1);
      color: #1e293b;
      transform: translateY(-1px);
    }
  `}

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  margin-top: 15px;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const FormCard = styled.div`
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.95);
  /* backdrop-filter: blur(20px); */
  backdrop-filter: none;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
  z-index: unset;
`;

type IGallerySettingsProps = {
  navigate: NavigateFunction;
};
const GallerySettings = observer(({ navigate }: IGallerySettingsProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Load gallery settings on component mount
  React.useEffect(() => {
    const loadSettings = async () => {
      setShowLoader(true);
      await fundraiserStore.loadGallerySettings();
      setShowLoader(false);
    };

    loadSettings();
  }, []);

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
      return;
    }
    // fundraiserStore.setView("gallery");
    navigate("/fundraiser/gallery");
  };

  const handleSave = async () => {
    const isValidationSuccess = fundraiserStore.gallerySettings.validate();
    if (!isValidationSuccess) {
      fundraiserStore.setGallerySettings(
        new GallerySettingsModel({ ...fundraiserStore.gallerySettings })
      );
      return false;
    }
    let isSaved = false;
    if (fundraiserStore.gallerySettings.id) {
      const updateResponse = await firstValueFrom(
        FundraiserService.updateGallerySettings(
          fundraiserStore.gallerySettings.createUpdateAPIModel()
        )
      );
      isSaved = updateResponse.result;
    } else {
      const saveResponse = await firstValueFrom(
        FundraiserService.saveGallerySettings(
          fundraiserStore.gallerySettings.createAPIModel()
        )
      );
      isSaved = saveResponse.result !== null;

      if (!fundraiserStore.gallerySettings.id && isSaved) {
        fundraiserStore.updateGallerySettings("id", saveResponse.result);
      }
    }

    if (isSaved) {
      setHasUnsavedChanges(false);
      ToastService.showSuccess(
        fundraiserStore.gallerySettings.id
          ? "Update successful"
          : "Gallery settings saved successfully"
      );
      return true;
    } else {
      ToastService.showError("Failed to save gallery settings");
      return false;
    }
  };

  const handleSaveAndClose = async () => {
    const success = await handleSave();
    if (success) {
      setHasUnsavedChanges(false);
      sessionStorage.setItem("gallerySettingsSaved", "true");
      // fundraiserStore.setView("gallery");
      navigate("/fundraiser/gallery");
    }
  };

  const handlePreview = () => {
    // fundraiserStore.setView("gallery");
    navigate("/fundraiser/gallery");
  };

  const handleSettingsChange = () => {
    setHasUnsavedChanges(true);
  };

  const handleUnsavedChangesEdit = () => {
    setShowUnsavedChangesModal(false);
    // Stay on the current page
  };

  const handleUnsavedChangesSave = async () => {
    const success = await handleSave();
    if (success) {
      setHasUnsavedChanges(false);
      // Set flag for gallery to show success toast
      sessionStorage.setItem("gallerySettingsSaved", "true");
      // fundraiserStore.setView("gallery");
      navigate("/fundraiser/gallery");
    } else {
      ToastService.showError("Failed to save gallery settings");
    }
    setShowUnsavedChangesModal(false);
  };

  const handleUnsavedChangesContinue = () => {
    setShowUnsavedChangesModal(false);
    // fundraiserStore.setView("gallery");
    navigate("/fundraiser/gallery");
  };

  const closeUnsavedChangesModal = () => {
    setShowUnsavedChangesModal(false);
  };

  const handleSelectLibraryMedia = (media: MediaItem) => {
    fundraiserStore.setGallerySettings(
      new GallerySettingsModel({
        ...fundraiserStore.gallerySettings,
        showLibrary: false,
        iconMedia: MediaModel.mediaItemToMediaModel(media),
      })
    );
  };

  if (fundraiserStore.gallerySettings?.showLibrary) {
    return (
      <div style={{ height: "100vh", width: "100vw" }}>
        <FullLibraryView
          onSelectLibraryMedia={(media: MediaItem) =>
            handleSelectLibraryMedia(media)
          }
          toggleLibrary={(show: boolean) =>
            fundraiserStore.updateGallerySettings("showLibrary", show)
          }
          allowOnlyOne={true}
        />
      </div>
    );
  }
  return (
    <SettingsContainer>
      <HeaderContainer isScrolled={isScrolled}>
        <Header>
          <LeftSection>
            <BackButton onClick={handleBackClick}>
              <ArrowLeft size={20} />
            </BackButton>
            <Title>Gallery Settings</Title>
          </LeftSection>

          <ActionButtons>
            <ActionButton onClick={handlePreview}>
              <Eye size={18} />
              Preview
            </ActionButton>
            <ActionButton onClick={handleSave}>
              <Save size={18} />
              Save
            </ActionButton>
            <ActionButton variant="primary" onClick={handleSaveAndClose}>
              <Save size={18} />
              Save & Close
            </ActionButton>
          </ActionButtons>
        </Header>
      </HeaderContainer>

      {showLoader ? (
        <>{new Loader(true).loaderComponent}</>
      ) : (
        <ContentWrapper>
          <ContentGrid>
            <LeftColumn>
              <FormCard>
                <GalleryAppearanceSection
                  onSettingsChange={handleSettingsChange}
                  gallerySettings={fundraiserStore.gallerySettings}
                />
              </FormCard>

              <FormCard>
                <GalleryContentSection
                  onSettingsChange={handleSettingsChange}
                />
              </FormCard>
            </LeftColumn>
          </ContentGrid>
        </ContentWrapper>
      )}

      {/* Unsaved Changes Modal */}
      {showUnsavedChangesModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "24px",
              padding: "40px",
              width: "90%",
              maxWidth: "450px",
              maxHeight: "90vh",
              overflowY: "auto",
              position: "relative",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
          >
            <button
              onClick={closeUnsavedChangesModal}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                border: "none",
                background: "#f1f5f9",
                color: "#64748b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 10,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e2e8f0";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f1f5f9";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <X size={20} />
            </button>

            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 24px 0",
                textAlign: "center",
              }}
            >
              Unsaved Changes
            </h3>

            <p
              style={{
                color: "#64748b",
                lineHeight: "1.6",
                marginBottom: "24px",
                fontSize: "16px",
                textAlign: "center",
              }}
            >
              You have made changes to gallery settings that haven't been saved
              yet. What would you like to do?
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                marginTop: "24px",
              }}
            >
              <button
                onClick={handleUnsavedChangesContinue}
                style={{
                  padding: "12px 24px",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: "none",
                  background: "#dc2626",
                  color: "#ffffff",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#b91c1c";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#dc2626";
                }}
              >
                Do Not Save
              </button>
              <button
                onClick={handleUnsavedChangesEdit}
                style={{
                  padding: "12px 24px",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: "none",
                  background: "#f59e0b",
                  color: "#ffffff",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#d97706";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f59e0b";
                }}
              >
                Edit
              </button>
              <button
                onClick={handleUnsavedChangesSave}
                style={{
                  padding: "12px 24px",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: "none",
                  background: "#10b981",
                  color: "#ffffff",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#059669";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#10b981";
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </SettingsContainer>
  );
});

export default withRouter(GallerySettings);
